import { salesforceHost } from '~/services/common';

import { type BuildDetailsFieldsParams, readOnlyField } from '../fieldHelpers';
import type { DetailField } from '../types';

type OpportunityInfoWithCustomLinks = App.OpportunityInfo & {
  view_in_admin_le__c?: string;
  view_in_le__c?: string;
  view_in_scoopon__c?: string;
  view_vendor_in_admin__c?: string;
};

type SalesforceWebLink = {
  label: string;
  template: string;
};

const ESCAPES_APPROVED_CUSTOM_LINKS: Array<SalesforceWebLink> = [
  {
    label: 'FX Price Adjustments',
    template:
      'https://luxgroup.lightning.force.com/lightning/r/Report/00O2y000002MBcE/view?queryScope=userFolders&fv0={!Opportunity.Id}',
  },
  { label: 'Hidden Fields', template: '/00O0I00000ALTpn?pv0={!Opportunity.Id}' },
  { label: 'NPS Results', template: '/00O2y000000hkOw?pv0={!Opportunity.X18_Character_ID__c}' },
  {
    label: 'Room Assets',
    template:
      'https://luxgroup.my.salesforce.com/lightning/r/Report/00O2y000001lyGmEAI/view?queryScope=userFolders&fv0={!Opportunity.Id}',
  },
  { label: 'Current Deal Performance', template: '/00O0I00000ALWZT?pv0={!Opportunity.Id}' },
  {
    label: 'Performance in Similar Deals',
    template:
      '/00O0I00000ALUue?pv0={!Opportunity.AC_Deal_Category__c}&pv1={!Opportunity.AC_Deal_Sub_Category__c}&pv2={!Opportunity.Destination__c}&pv3={!Opportunity.Sub_Destination__c}',
  },
];

const ESCAPES_PRE_DEAL_REVIEW_CUSTOM_LINKS: Array<SalesforceWebLink> = [
  { label: 'Hidden Fields', template: '/00O0I00000ALTpn?pv0={!Opportunity.Id}' },
  {
    label: 'Experiences in Destination',
    template:
      'https://luxgroup.lightning.force.com/lightning/r/Report/00O2y000000MANwEAO/view?queryScope=userFolders&fv1={!Opportunity.Destination__c}&fv2={!Opportunity.Expiry_date_Travel_From__c}&fv3={!Opportunity.Travel_To__c}',
  },
  {
    label: 'Performance in Similar Deals',
    template:
      '/00O0I00000ALUue?pv0={!Opportunity.AC_Deal_Category__c}&pv1={!Opportunity.AC_Deal_Sub_Category__c}&pv2={!Opportunity.Destination__c}&pv3={!Opportunity.Sub_Destination__c}',
  },
];

const ESCAPES_POST_DEAL_REVIEW_CUSTOM_LINKS: Array<SalesforceWebLink> = [
  { label: 'Hidden Fields', template: '/00O0I00000ALTpn?pv0={!Opportunity.Id}' },
  {
    label: 'Room Assets',
    template:
      'https://luxgroup.my.salesforce.com/lightning/r/Report/00O2y000001lyGmEAI/view?queryScope=userFolders&fv0={!Opportunity.Id}',
  },
  {
    label: 'Performance in Similar Deals',
    template:
      '/00O0I00000ALUue?pv0={!Opportunity.AC_Deal_Category__c}&pv1={!Opportunity.AC_Deal_Sub_Category__c}&pv2={!Opportunity.Destination__c}&pv3={!Opportunity.Sub_Destination__c}',
  },
];

const CUSTOM_LINKS_BY_RECORD_TYPE: Record<string, Array<SalesforceWebLink>> = {
  'Escapes - Pre Deal Review': ESCAPES_PRE_DEAL_REVIEW_CUSTOM_LINKS,
  'Escapes - Post Deal Review': ESCAPES_POST_DEAL_REVIEW_CUSTOM_LINKS,
  'Escapes Approved': ESCAPES_APPROVED_CUSTOM_LINKS,
};

function extractSalesforceFormulaLink(value?: string) {
  const hrefMatch = value?.match(/\bhref=(["'])(.*?)\1/i);

  return hrefMatch?.[2] ?? value;
}

function getOpportunityMergeValues(opportunity?: App.OpportunityPage, opportunityId?: string) {
  const keyDates = opportunity?.key_dates;
  const location = opportunity?.location;

  return {
    'Opportunity.AC_Deal_Category__c': location?.ac_deal_category__c,
    'Opportunity.AC_Deal_Sub_Category__c': location?.ac_deal_sub_category__c,
    'Opportunity.Destination__c': location?.destination__c,
    'Opportunity.Expiry_date_Travel_From__c': keyDates?.expiry_date_travel_from__c,
    'Opportunity.Id': opportunityId,
    'Opportunity.Sub_Destination__c': location?.sub_destination__c,
    'Opportunity.Travel_To__c': keyDates?.travel_to__c,
    'Opportunity.X18_Character_ID__c': opportunityId,
  };
}

function buildSalesforceWebLinkUrl(template: string, mergeValues: Record<string, string | undefined>) {
  const path = template.replace(/\{!(.*?)\}/g, (_match, mergeField: string) =>
    encodeURIComponent(mergeValues[mergeField] ?? ''),
  );

  if (path.startsWith('/')) {
    return salesforceHost ? `${salesforceHost}${path}` : undefined;
  }

  return path;
}

function buildSalesforceCustomLinkFields({
  offer,
  opportunity,
  opportunityId,
}: BuildDetailsFieldsParams): Array<DetailField> {
  const customLinks = CUSTOM_LINKS_BY_RECORD_TYPE[offer?.salesforce_record_type ?? ''] ?? [];
  const mergeValues = getOpportunityMergeValues(opportunity, opportunityId);

  return customLinks.map((customLink) =>
    readOnlyField(customLink.label, buildSalesforceWebLinkUrl(customLink.template, mergeValues)),
  );
}

export function buildCustomLinksFields(params: BuildDetailsFieldsParams): Array<DetailField> {
  const { opportunity } = params;
  const opportunityInfo = opportunity?.opportunity_info as OpportunityInfoWithCustomLinks | undefined;

  return [
    readOnlyField('View in LE', extractSalesforceFormulaLink(opportunityInfo?.view_in_le__c)),
    readOnlyField('View In Scoopon', extractSalesforceFormulaLink(opportunityInfo?.view_in_scoopon__c)),
    readOnlyField('View Offer in Admin', extractSalesforceFormulaLink(opportunityInfo?.view_in_admin_le__c)),
    readOnlyField('View Vendor in Admin', extractSalesforceFormulaLink(opportunityInfo?.view_vendor_in_admin__c)),
    ...buildSalesforceCustomLinkFields(params),
  ];
}
